import React, { useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import { HubPrimaryAction } from "../../shared/HubPrimaryAction";
import { PAGE_LAYOUT } from "../../../layout/pageLayout";
import { DesktopKpiStrip } from "../../shared/DesktopKpiStrip";
import { DeskEmpty, DeskSearch, DeskTabs } from "../../desktop/DeskTable";
import { RecIcon } from "../../materials/MaterialRecordParts";
import {
  ESTIMATE_ACTIVE_HUB_STATUSES,
  ESTIMATE_STATUS_OPTIONS,
  formatEstimateStatus,
  resolveEstimateStatus,
} from "../../../helpers/estimates";
import { formatCurrency } from "../../../helpers/estimateTotals";
import "../../project/projects.css";
import "./estimateDesktop.css";
import { estimateStatusTone, formatShortDate } from "./estimateDesktopUi";

const QUICK_ARCHIVE_STATUSES = new Set(["approved", "converted", "declined"]);
const ALL_TAB = "all";
const SORTS = [
  { value: "updated", label: "Recently updated" },
  { value: "name", label: "Name (A–Z)" },
  { value: "price", label: "Price: high to low" },
];

function clientKey(row) {
  return row.client_id || row.clients?.client_name || "";
}

/** Estimate card: the Project card's design language, built only from real estimate fields. */
function EstimateCard({ row, onOpen, onArchive, archiving }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const status = resolveEstimateStatus(row.status);
  const tone = estimateStatusTone(row.status);
  const name = row.title || "Untitled Estimate";
  const canArchive = QUICK_ARCHIVE_STATUSES.has(status);
  const hasPrice = row.final_estimate_price != null && row.final_estimate_price !== "";

  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown") {
        if (event.key === "Escape") setMenuOpen(false);
        return;
      }
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  return (
    <div className={`pj-card est-card${status === "archived" ? " is-archived" : ""}`} data-testid="estimate-card">
      <div className="pj-card__row">
        <button
          type="button"
          className="pj-card__main"
          onClick={() => onOpen(row.estimate_id)}
          aria-label={`Open ${name}`}
        >
          <span className="pj-card__tile" aria-hidden="true">
            <RecIcon name="doc" />
          </span>
          <span className="pj-card__text">
            <span className="pj-card__name">{name}</span>
            {row.clients?.client_name ? <span className="pj-card__client">{row.clients.client_name}</span> : null}
            <span className="pj-card__meta">
              <span className={`pj-status est-status--${tone}`}>{formatEstimateStatus(row.status)}</span>
            </span>
            <span className="est-card__price">
              <em>Final price</em>
              <b>{hasPrice ? formatCurrency(row.final_estimate_price) : "Not set"}</b>
            </span>
          </span>
        </button>
        <RecIcon name="chevron" className="pj-card__chevron" />
        {canArchive ? (
          <div className="pj-card__actions">
            <div className="pj-menuwrap" ref={menuRef}>
              <button
                type="button"
                className="pj-iconbtn"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`More actions for ${name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuOpen((open) => !open);
                }}
              >
                <RecIcon name="more" className="pj-more-icon" />
              </button>
              {menuOpen ? (
                <div className="mat-menu pj-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="mat-menu__item pj-menu-danger"
                    onClick={() => {
                      setMenuOpen(false);
                      if (!archiving) onArchive(row);
                    }}
                  >
                    <span>{archiving ? "Archiving…" : "Archive estimate"}</span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <div className="pj-card__foot">
        <span>
          <em>Estimate #</em>
          <b>{row.estimate_number || "—"}</b>
        </span>
        <span>
          <em>Updated</em>
          <b>{formatShortDate(row.updated_at)}</b>
        </span>
        <span>
          <em>Created</em>
          <b>{formatShortDate(row.created_at)}</b>
        </span>
      </div>
    </div>
  );
}

/**
 * Desktop Estimates workspace — the Projects landing's sibling: header,
 * search / client / sort toolbar, KPI strip, status tabs and a card grid.
 * Counts and the client list come from the estimates already loaded.
 */
export function EstimateHubDesktop({
  onBack,
  catalogReady = true,
  estimates,
  statusFilter,
  onStatusFilter,
  searchTerm,
  onSearch,
  filtered,
  onNew,
  onOpen,
  onArchive,
  archivingEstimateId,
  children,
}) {
  const [clientFilter, setClientFilter] = useState("");
  const [sortKey, setSortKey] = useState("updated");

  const clients = useMemo(() => {
    const map = new Map();
    estimates.forEach((row) => {
      const key = clientKey(row);
      const label = row.clients?.client_name;
      if (key && label && !map.has(key)) map.set(key, label);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [estimates]);

  const scoped = clientFilter ? estimates.filter((row) => clientKey(row) === clientFilter) : estimates;
  const statusOf = (row) => resolveEstimateStatus(row.status);
  const countFor = (value) => scoped.filter((row) => statusOf(row) === value).length;
  const activeCount = scoped.filter((row) => ESTIMATE_ACTIVE_HUB_STATUSES.includes(statusOf(row))).length;

  const tabs = [
    { value: ALL_TAB, label: "All", count: activeCount },
    ...ESTIMATE_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label, count: countFor(opt.value) })),
  ];

  const visible = useMemo(() => {
    const rows = clientFilter ? filtered.filter((row) => clientKey(row) === clientFilter) : [...filtered];
    if (sortKey === "name") {
      rows.sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
    } else if (sortKey === "price") {
      rows.sort((a, b) => Number(b.final_estimate_price || 0) - Number(a.final_estimate_price || 0));
    }
    return rows;
  }, [filtered, clientFilter, sortKey]);

  const activeTab = statusFilter || ALL_TAB;
  const activeLabel = tabs.find((t) => t.value === activeTab)?.label || "";
  const clientLabel = clients.find(([key]) => key === clientFilter)?.[1];

  return (
    <CrewPilotModuleShell
      title="Estimates"
      subtitle="Create and manage job estimates."
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
    >
      {!catalogReady ? <div className="dt-error">Catalog unavailable.</div> : null}

      <div className="est-hub-controls">
        <label className="dt-select est-hub-select">
          <span className="dt-sr">Client</span>
          <select value={clientFilter} onChange={(event) => setClientFilter(event.target.value)} aria-label="Filter by client">
            <option value="">All Clients</option>
            {clients.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="dt-select est-hub-select">
          <span className="dt-sr">Sort</span>
          <select value={sortKey} onChange={(event) => setSortKey(event.target.value)} aria-label="Sort estimates">
            {SORTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <DeskSearch value={searchTerm} onChange={onSearch} placeholder="Search estimates…" ariaLabel="Search estimates" width="100%" />
        <HubPrimaryAction onClick={onNew} label="New Estimate" ariaLabel="+ New Estimate" />
      </div>

      <DesktopKpiStrip
        ariaLabel="Estimate pipeline"
        className="est-d-kpi5"
        tiles={[
          { key: "active", value: String(activeCount), label: "Active", tone: "gold" },
          { key: "draft", value: String(countFor("draft")), label: "Draft", tone: "muted" },
          { key: "ready", value: String(countFor("ready")), label: "Ready", tone: "gold" },
          { key: "sent", value: String(countFor("sent")), label: "Sent", tone: "blue" },
          { key: "approved", value: String(countFor("approved")), label: "Approved", tone: "green" },
        ]}
      />

      <div className="est-hub-status">
        <DeskTabs
          options={tabs}
          value={activeTab}
          onChange={(value) => onStatusFilter(value === ALL_TAB ? null : value)}
          ariaLabel="Filter by status"
        />
        <span className="dt-summary" aria-live="polite">
          {visible.length} {visible.length === 1 ? "estimate" : "estimates"}
          {clientLabel ? ` · ${clientLabel}` : ""}
        </span>
      </div>

      {visible.length === 0 ? (
        <DeskEmpty title={estimates.length === 0 ? "No estimates yet" : "No estimates found"}>
          {estimates.length === 0
            ? "Create your first estimate to start your pipeline."
            : searchTerm.trim() || clientFilter
              ? "No estimates match your search and filters."
              : `No ${activeLabel.toLowerCase()} estimates.`}
        </DeskEmpty>
      ) : (
        <div className="est-card-grid" data-testid="estimates-grid">
          {visible.map((row) => (
            <EstimateCard
              key={row.estimate_id}
              row={row}
              onOpen={onOpen}
              onArchive={onArchive}
              archiving={archivingEstimateId === row.estimate_id}
            />
          ))}
        </div>
      )}
      {children}
    </CrewPilotModuleShell>
  );
}
